import {
  Box,
  Button,
  Grid,
  IconButton,
  styled,
  Tooltip,
  Typography
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { Plus, Users } from "react-feather";
import { ReactComponent as Edit } from "../../assets/edit.svg";
import { ReactComponent as TrashCan } from "../../assets/trash.svg";
import { AllowancesForm } from "../../components/moduleForms/Allowances/AllowancesForm";
import { AccountSummary } from "../../components/shared/AccountSummary";
import { ColouredContainer } from "../../components/shared/ColoredContainer";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { PageBlurb } from "../../components/shared/PageBlurb";
import { PageHeader } from "../../components/shared/PageHeader";
import { SideDrawer } from "../../components/shared/SideDrawer";
import { PageHeaderControlContext } from "../../context/PageHeaderControlContext";
import { useDeletePlan } from "../../hooks/plans/useDeletePlan";
import { MembershipType, Plan } from "../../models";
import { EmployerModuleType } from "../../models/accounts/employerModuleType";
import { isLoading } from "../../utils/isLoading";
import { extraordinaryColors } from "../../utils/theme";
import { useData } from "../../utils/useData";

export const StyledEditIcon = styled(Edit)({ cursor: "pointer" });
const StyledTrash = styled(TrashCan)({ marginLeft: "auto", cursor: "pointer" });

const UsersIcon = styled(Users)({ color: "white", marginRight: 8 });

export const PlanStatistics = ({ totalUsers }: { totalUsers: Number }) => {
  const totalUserDisplay = `${totalUsers} Active Users`;

  return (
    <Box display={"flex"} flexDirection={"row"} paddingY={2}>
      <UsersIcon />
      <Typography marginRight={2}>{totalUserDisplay}</Typography>
    </Box>
  );
};

export const DeletePlanButton = ({
  totalUsers,
  onClick
}: {
  totalUsers: number;
  onClick: Function;
}) => {
  return (
    <Tooltip
      title={
        totalUsers === 0
          ? "Delete"
          : "Cannot delete plan - Employees are assigned to it."
      }
    >
      <div>
        <IconButton disabled={totalUsers !== 0} onClick={onClick as any}>
          <StyledTrash />
        </IconButton>
      </div>
    </Tooltip>
  );
};

export const Allowances = () => {
  const { setHeaderActions } = useContext(PageHeaderControlContext);

  const { plans, membership, serviceSectors, accounts } = useData();

  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [editingPlan, setEditingPlan] = useState<Plan | undefined>(undefined);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const mutationDeletePlan = useDeletePlan();

  const planTotalUsersMap: Record<string, number> = useMemo(() => {
    const planMap: Record<string, number> = {};
    accounts.data?.items.forEach((account) => {
      planMap[account.planId] ??= 0;
      planMap[account.planId] += 1;
    });

    return planMap;
  }, [accounts.data]);

  const isPilot = membership.data?.type === MembershipType.PILOT;
  const planTotalItems = plans?.data?.totalItems ?? 0;
  const canAddPlans = isPilot
    ? true
    : membership.data?.features?.maxPlanCount
    ? planTotalItems >= membership.data?.features?.maxPlanCount
    : false;

  useEffect(() => {
    setHeaderActions([
      <Button
        variant="contained"
        onClick={() => setIsAdding(true)}
        startIcon={<Plus size={16} />}
        size="medium"
        disabled={canAddPlans}
      >
        New
      </Button>
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading([plans, serviceSectors])) return <LoadingSpinner />;

  if (plans.error || !plans.data) {
    return <p>Something went wrong, please try again later</p>;
  }

  const renderPlan = (plan: Plan) => {
    return (
      <Grid item xs={12} md={6} key={plan.id}>
        <ColouredContainer
          title={plan.name}
          color={"#E6A236"}
          toolbarItems={
            <>
              <DeletePlanButton
                totalUsers={planTotalUsersMap[plan.id] ?? 0}
                onClick={() => mutationDeletePlan.mutate(plan)}
              />
              <IconButton
                onClick={() => {
                  setIsEditing(true);
                  setEditingPlan(plan);
                }}
              >
                <StyledEditIcon />
              </IconButton>
            </>
          }
          subtitleItems={
            <PlanStatistics totalUsers={planTotalUsersMap[plan.id] ?? 0} />
          }
        >
          <Box sx={{ padding: 2 }}>
            <AccountSummary
              variant="fullColor"
              planInfo={{
                ...plan,
                planType: plan.type,
                serviceSectorIds:
                  plan.controls.serviceSectors?.map((ssref) => ssref.id) ?? []
              }}
              serviceSectors={serviceSectors.data?.items}
            />
          </Box>
        </ColouredContainer>
      </Grid>
    );
  };

  return (
    <>
      <PageHeader pageTitle="Allowances" />
      <PageBlurb
        description="Set spending limits by category, load funds instantly, and give employees access to what they need, without out-of-pocket costs or reimbursement delays. Everything is tracked and reported automatically, so you stay in control while removing friction for your team."
        borderColor={extraordinaryColors.yellow.main}
      />

      <Grid container spacing={2}>
        {plans.data.items
          .filter(
            (p) =>
              p.controls.purchases.isEnabled &&
              !p.controls.payments.isEnabled &&
              p.employerModuleOptions.employerModuleType ===
                EmployerModuleType.ALLOWANCES
          )
          .map(renderPlan)}
      </Grid>

      <SideDrawer title="Add Allowance" open={isAdding} setIsOpen={setIsAdding}>
        <AllowancesForm onClose={() => setIsAdding(false)} />
      </SideDrawer>
      <SideDrawer
        title="Edit Allowance"
        open={isEditing}
        setIsOpen={setIsEditing}
      >
        <AllowancesForm
          plan={editingPlan!}
          onClose={() => setIsEditing(false)}
        />
      </SideDrawer>
    </>
  );
};
